import React from 'react';
import Cache from 'utils/cache';
import Color from 'utils/color';
import SoftwareUpdateService from 'services/softwareUpdateService';
import _ from 'lodash';

function siteFilterFn(node) {
  return node.agentId > 0;
};

async function getSoftwareTypes() {
  const types = Cache.getPersistant('softwareUpdate_types')
  if (types) {
    return types;
  }
  else {
    const newTypes = await SoftwareUpdateService.API.getSoftwareTypes();
    Cache.setPersistant('softwareUpdate_types', newTypes);
    return newTypes;
  }
}

async function getSoftwareVersions() {
  const versions = Cache.getTemporary('softwareUpdate_versions')
  if (versions) {
    return versions;
  }
  else {
    const newVersions = await SoftwareUpdateService.API.getSoftwareVersions(false);
    const newVersionsGrouped = _.groupBy(newVersions, 'typeId');
    Cache.setTemporary('softwareUpdate_versions', newVersionsGrouped);
    return newVersionsGrouped;
  }
}

async function getSoftwareVersionsForUpdate() {
  const versions = Cache.getTemporary('softwareUpdate_versionsForUpdate')
  if (versions) {
    return versions;
  }
  else {
    const newVersions = await SoftwareUpdateService.API.getSoftwareVersions(true);
    const newVersionsGrouped = _.groupBy(newVersions, 'typeId');
    Cache.setTemporary('softwareUpdate_versionsForUpdate', newVersionsGrouped);
    return newVersionsGrouped;
  }
}

async function getSoftwareUpdateStatuses() {
  const statuses = Cache.getTemporary('softwareUpdate_statuses')
  if (statuses) {
    return statuses;
  }
  else {
    const newStatuses = await SoftwareUpdateService.API.getSoftwareUpdateStatuses();
    Cache.setTemporary('softwareUpdate_statuses', newStatuses);
    return newStatuses;
  }
}

async function getSoftwareUpdateDetailsStatuses() {
  const statuses = Cache.getTemporary('softwareUpdate_detailStatuses')
  if (statuses) {
    return statuses;
  }
  else {
    const newStatuses = await SoftwareUpdateService.API.getSoftwareUpdateDetailsStatuses();
    Cache.setTemporary('softwareUpdate_detailStatuses', newStatuses);
    return newStatuses;
  }
}

const SoftwareUpdateHelper = {
  siteFilterFn,
  getSoftwareTypes,
  getSoftwareVersions,
  getSoftwareVersionsForUpdate,
  getSoftwareUpdateStatuses,
  getSoftwareUpdateDetailsStatuses,

  enums: {
    softwareTypes: {
      PoselinkAgent: 1,
      SpcWin: 2,
      QuickMenu: 3,
      MozartPlugin: 4,
      MozartAgent: 5,
      POSiPay: 6,
      TimeAttendanceUtilities: 7,
    },
    softwareUpdateStatus: {
      UpdateCanceled: -1,
      OnHold: 1,
      WaitingToDistribute: 2,
      DeliveryInProgress: 3,
      DeliveryInProgressWithErrors: 4,
      Completed: 5,
      CompletedWithErrors: 6,
    },
    softwareUpdateDtlStatus: {
      CannotInstall: -3,
      MissingAgent: -2,
      Canceled: -1,
      WaitingToUpdate: 0,
      UpdateInProgress: 1,
      UpdateFailed: 2,
      UpdateTimedOut: 3,
      Completed: 4,
      OnHold: 5,
      UpdateTimedOutNeverAttempted: 6,
    }
  },

  getUpdateStatusIcon: function (status) {
    switch (status) {
      case this.enums.softwareUpdateStatus.UpdateCanceled:
        return <i className='icons8-stop' />;
      case this.enums.softwareUpdateStatus.OnHold:
        return <i className='icons8-pause' />;
      case this.enums.softwareUpdateStatus.WaitingToDistribute:
        return <i className='icons8-clock' />;
      case this.enums.softwareUpdateStatus.DeliveryInProgress:
        return <i className='icons8-submit-progress' />;
      case this.enums.softwareUpdateStatus.DeliveryInProgressWithErrors:
        return <i className='icons8-submit-progress' />;
      case this.enums.softwareUpdateStatus.Completed:
        return <i className='icons8-checkmark' />;
      case this.enums.softwareUpdateStatus.CompletedWithErrors:
        return <i className='icons8-checkmark' />;
      default:
        return <></>;
    }
  },

  getUpdateStatusColor: function (status) {
    //BACKGROUND COLOR w/ white text
    switch (status) {
      case this.enums.softwareUpdateStatus.UpdateCanceled:
        return Color.defaults.danger;
      case this.enums.softwareUpdateStatus.OnHold:
        return '#a9a9a9';
      case this.enums.softwareUpdateStatus.WaitingToDistribute:
        return Color.defaults.primary;
      case this.enums.softwareUpdateStatus.DeliveryInProgress:
        return Color.defaults.secondary;
      case this.enums.softwareUpdateStatus.DeliveryInProgressWithErrors:
        return Color.defaults.secondary;
      case this.enums.softwareUpdateStatus.Completed:
        return Color.defaults.success;
      case this.enums.softwareUpdateStatus.CompletedWithErrors:
        return Color.defaults.success;
      default:
        return '#000000';
    }
  },

  getUpdateDtlStatusColor: function (status) {
    //TEXT COLOR w/ white background
    switch (status) {
      case this.enums.softwareUpdateDtlStatus.CannotInstall:
        return Color.defaults.danger;
      case this.enums.softwareUpdateDtlStatus.MissingAgent:
        return Color.defaults.danger;
      case this.enums.softwareUpdateDtlStatus.Canceled:
        return '#a9a9a9';
      case this.enums.softwareUpdateDtlStatus.WaitingToUpdate:
        return Color.defaults.primary;
      case this.enums.softwareUpdateDtlStatus.UpdateInProgress:
        return Color.defaults.secondary;
      case this.enums.softwareUpdateDtlStatus.UpdateFailed:
        return Color.defaults.danger;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOut:
        return Color.defaults.danger;
      case this.enums.softwareUpdateDtlStatus.Completed:
        return Color.defaults.success;
      case this.enums.softwareUpdateDtlStatus.OnHold:
        return '#a9a9a9';
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOutNeverAttempted:
        return Color.defaults.danger;
      default:
        return '#000000';
    }
  },

  canUpdateEdit: function (status) {
    switch (status) {
      case this.enums.softwareUpdateStatus.UpdateCanceled:
        return false;
      case this.enums.softwareUpdateStatus.OnHold:
        return true;
      case this.enums.softwareUpdateStatus.WaitingToDistribute:
        return true;
      case this.enums.softwareUpdateStatus.DeliveryInProgress:
        return true;
      case this.enums.softwareUpdateStatus.DeliveryInProgressWithErrors:
        return true;
      case this.enums.softwareUpdateStatus.Completed:
        return false;
      case this.enums.softwareUpdateStatus.CompletedWithErrors:
        return false;
      default:
        return false;
    }
  },

  canUpdateRefresh: function (status) {
    switch (status) {
      case this.enums.softwareUpdateStatus.UpdateCanceled:
        return false;
      case this.enums.softwareUpdateStatus.OnHold:
        return false;
      case this.enums.softwareUpdateStatus.WaitingToDistribute:
        return true;
      case this.enums.softwareUpdateStatus.DeliveryInProgress:
        return true;
      case this.enums.softwareUpdateStatus.DeliveryInProgressWithErrors:
        return true;
      case this.enums.softwareUpdateStatus.Completed:
        return false;
      case this.enums.softwareUpdateStatus.CompletedWithErrors:
        return false;
      default:
        return false;
    }
  },

  canUpdateCancel: function (status) {
    switch (status) {
      case this.enums.softwareUpdateStatus.UpdateCanceled:
        return false;
      case this.enums.softwareUpdateStatus.OnHold:
        return false;
      case this.enums.softwareUpdateStatus.WaitingToDistribute:
        return true;
      case this.enums.softwareUpdateStatus.DeliveryInProgress:
        return true;
      case this.enums.softwareUpdateStatus.DeliveryInProgressWithErrors:
        return true;
      case this.enums.softwareUpdateStatus.Completed:
        return false;
      case this.enums.softwareUpdateStatus.CompletedWithErrors:
        return false;
      default:
        return false;
    }
  },

  canUpdateDelete: function (status) {
    switch (status) {
      case this.enums.softwareUpdateStatus.UpdateCanceled:
        return true;
      case this.enums.softwareUpdateStatus.OnHold:
        return true;
      case this.enums.softwareUpdateStatus.WaitingToDistribute:
        return false;
      case this.enums.softwareUpdateStatus.DeliveryInProgress:
        return false;
      case this.enums.softwareUpdateStatus.DeliveryInProgressWithErrors:
        return false;
      case this.enums.softwareUpdateStatus.Completed:
        return false;
      case this.enums.softwareUpdateStatus.CompletedWithErrors:
        return false;
      default:
        return false;
    }
  },

  canUpdateDtlRetry: function (status) {
    switch (status) {
      case this.enums.softwareUpdateDtlStatus.CannotInstall:
        return false;
      case this.enums.softwareUpdateDtlStatus.MissingAgent:
        return false;
      case this.enums.softwareUpdateDtlStatus.Canceled:
        return false;
      case this.enums.softwareUpdateDtlStatus.WaitingToUpdate:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateInProgress:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateFailed:
        return true;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOut:
        return true;
      case this.enums.softwareUpdateDtlStatus.Completed:
        return false;
      case this.enums.softwareUpdateDtlStatus.OnHold:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOutNeverAttempted:
        return true;
      default:
        return false;
    }
  },


  canUpdateDtlCancel: function (status) {
    switch (status) {
      case this.enums.softwareUpdateDtlStatus.CannotInstall:
        return false;
      case this.enums.softwareUpdateDtlStatus.MissingAgent:
        return false;
      case this.enums.softwareUpdateDtlStatus.Canceled:
        return false;
      case this.enums.softwareUpdateDtlStatus.WaitingToUpdate:
        return true;
      case this.enums.softwareUpdateDtlStatus.UpdateInProgress:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateFailed:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOut:
        return false;
      case this.enums.softwareUpdateDtlStatus.Completed:
        return false;
      case this.enums.softwareUpdateDtlStatus.OnHold:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOutNeverAttempted:
        return false;
      default:
        return false;
    }
  },

  canUpdateDtlDelete: function (status) {
    switch (status) {
      case this.enums.softwareUpdateDtlStatus.CannotInstall:
        return true;
      case this.enums.softwareUpdateDtlStatus.MissingAgent:
        return true;
      case this.enums.softwareUpdateDtlStatus.Canceled:
        return true;
      case this.enums.softwareUpdateDtlStatus.WaitingToUpdate:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateInProgress:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateFailed:
        return false;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOut:
        return false;
      case this.enums.softwareUpdateDtlStatus.Completed:
        return false;
      case this.enums.softwareUpdateDtlStatus.OnHold:
        return true;
      case this.enums.softwareUpdateDtlStatus.UpdateTimedOutNeverAttempted:
        return false;
      default:
        return false;
    }
  },

};

export default SoftwareUpdateHelper;